import React from 'react';
import { useI18n } from '@/lib/i18n';
import { ArrowRight, ArrowDown } from '@phosphor-icons/react';

const HERO_BG = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2000&q=80';

export default function Hero({ onApply }) {
  const { t } = useI18n();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen bg-[#0A1128] overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/85 via-[#0A1128]/85 to-[#0A1128]" />
      <div className="absolute inset-0 grain" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pt-36 pb-24 lg:pt-44 lg:pb-32 min-h-screen flex flex-col justify-center">
        <div className="max-w-5xl">
          <div className="eyebrow text-[#C5A059] mb-6 fade-up flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" />
            {t.hero.eyebrow}
          </div>
          <h1 className="heading-display text-white text-[30px] sm:text-[44px] md:text-[56px] lg:text-[80px] fade-up fade-up-d1 leading-[1.05]">
            {t.hero.title1}<br/>
            <span className="text-[#C5A059] italic font-light">{t.hero.title2}</span><br/>
            {t.hero.title3}
          </h1>
          <p className="mt-10 text-white/70 text-lg lg:text-xl leading-relaxed max-w-2xl fade-up fade-up-d2">
            {t.hero.subtitle}
          </p>

          <div className="mt-14 flex flex-wrap gap-4 fade-up fade-up-d3">
            <button
              data-testid="hero-cta-personnel"
              onClick={() => { scrollTo('contact'); window.dispatchEvent(new CustomEvent('set-inquiry', { detail: 'personnel' })); }}
              className="group inline-flex items-center gap-3 bg-[#C5A059] hover:bg-[#B38F4D] text-[#0A1128] px-8 py-4 text-[12px] font-semibold tracking-[0.22em] uppercase transition-all"
            >
              {t.hero.cta1}
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-orders"
              onClick={() => { scrollTo('contact'); window.dispatchEvent(new CustomEvent('set-inquiry', { detail: 'orders' })); }}
              className="inline-flex items-center gap-3 bg-transparent border border-white/25 hover:border-[#C5A059] hover:text-[#C5A059] text-white px-8 py-4 text-[12px] font-semibold tracking-[0.22em] uppercase transition-all"
            >
              {t.hero.cta2}
              <ArrowRight size={16} weight="bold" />
            </button>
            <button
              data-testid="hero-cta-apply"
              onClick={onApply}
              className="inline-flex items-center gap-3 bg-transparent border border-white/25 hover:border-[#C5A059] hover:text-[#C5A059] text-white px-8 py-4 text-[12px] font-semibold tracking-[0.22em] uppercase transition-all"
            >
              {t.hero.cta3}
            </button>
          </div>
        </div>
      </div>

      {/* Decorative right-side vertical text */}
      <div className="hidden xl:block absolute right-8 top-1/2 -translate-y-1/2 fade-up fade-up-d4">
        <div className="rotate-90 origin-right text-white/30 text-[11px] tracking-[0.5em] uppercase whitespace-nowrap">
          De Melo Recruiting — est. 2024
        </div>
      </div>

      <div className="absolute bottom-6 left-6 md:left-12 lg:left-16 flex items-center gap-3 text-white/40 text-[10px] tracking-[0.3em] uppercase">
        <span className="w-10 h-px bg-white/30" />
        <span>{t.hero.scroll}</span>
        <ArrowDown size={12} className="animate-bounce" />
      </div>
    </section>
  );
}
